import React, { useEffect, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BoltIcon from "@mui/icons-material/Bolt";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import dayjs from "dayjs";
import useAuth from "hooks/useAuth";
import utc from "dayjs/plugin/utc";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { visuallyHidden } from "@mui/utils";

dayjs.extend(utc);

const PRIMARY_COLOR = "#0666e5";
const SUCCESS_COLOR = "#4caf50";
const ERROR_COLOR = "#f44336";

const formatDateTime = (value) => {
  if (!value) return "-";
  return dayjs.utc(value).format("DD MMM YYYY, hh:mm:ss A");
};

const formatDuration = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = Number(value);
  if (!Number.isNaN(numericValue)) {
    if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(2)} sec`;
    return `${numericValue} s`;
  }
  return value;
};

const getCronInfo = (log) =>
  log?.error_message ||
  log?.response_body ||
  log?.info ||
  "No additional information available for this cron run.";

const getStatusColor = (status) => {
  if (!status) return "#757575";
  switch (status.toLowerCase()) {
    case "success":
      return SUCCESS_COLOR;
    case "failed":
      return ERROR_COLOR;
    case "pending":
      return "#ff9800";
    case "running":
      return "#2196f3";
    default:
      return "#757575";
  }
};

const CronDashboard = () => {
  const { getCronDashboardData } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [cronLogs, setCronLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("executed_time");
  const [usersearch, setUserSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expectedCronCount, setExpectedCronCount] = useState(0);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    let isMounted = true;
    const fetchCronLogs = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getCronDashboardData(
          selectedDate.format("YYYY-MM-DD"),
        );

        if (!isMounted) return;
        // Ensure we always set an array
        setCronLogs(Array.isArray(result?.data) ? result.data : []);
        setExpectedCronCount(result?.expected?.value || 0);
      } catch (fetchError) {
        if (!isMounted) return;
        setCronLogs([]);
        setError(
          fetchError?.response?.data?.message ||
            "Unable to fetch cron dashboard data.",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchCronLogs();
    return () => {
      isMounted = false;
    };
  }, [getCronDashboardData, selectedDate]);

  // Safely transform data – ensure cronLogs is an array
  const transformedData = (cronLogs || []).map((log, index) => ({
    id: index + 1,
    cronName: log?.cron_name || "Unknown Cron",
    status: log?.cron_status || "Unknown",
    lastRun: log?.executed_time || null,
    timeTaken: log?.time_taken || 0,
    errorMessage: log?.error_message || null,
    rawData: log,
  }));

  console.log("transformedData", transformedData);

  // Filter data – transformedData is always an array
  const filteredData = transformedData.filter((cron) => {
    const matchesSearch = cron.cronName
      .toLowerCase()
      .includes(usersearch.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || cron.status?.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  console.log("filteredData", filteredData);

  const count_success = transformedData.filter(
    (cron) => cron.status?.toLowerCase() === "success",
  ).length;
  const count_failure = transformedData.filter(
    (cron) => cron.status?.toLowerCase() === "failed",
  ).length;
  const totalAllCrons = transformedData.length;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const createSortHandler = (property) => () => handleRequestSort(property);

  const sortedData = [...filteredData].sort((a, b) => {
    if (orderBy === "lastRun") {
      const dateA = a[orderBy] ? new Date(a[orderBy]) : new Date(0);
      const dateB = b[orderBy] ? new Date(b[orderBy]) : new Date(0);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    if (orderBy === "timeTaken") {
      const timeA = a[orderBy] || 0;
      const timeB = b[orderBy] || 0;
      return order === "asc" ? timeA - timeB : timeB - timeA;
    }
    if (orderBy === "id") {
      return order === "asc"
        ? a[orderBy] - b[orderBy]
        : b[orderBy] - a[orderBy];
    }
    const valA = a[orderBy] || "";
    const valB = b[orderBy] || "";
    if (valA < valB) return order === "asc" ? -1 : 1;
    if (valA > valB) return order === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedData = sortedData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handlePageChange = (event, value) => setPage(value);
  const handleSearch = (e) => {
    setUserSearch(e.target.value);
    setPage(1);
  };
  const handleStatusFilter = (filter) => {
    setStatusFilter(filter);
    setPage(1);
  };
  const handleClearFilters = () => {
    setStatusFilter("all");
    setUserSearch("");
    setPage(1);
  };
  const handleViewDocumentation = () => {
    window.open(
      "https://docs.google.com/document/d/1Aiw3eI3C2BQjRtSvOpVgbUYMfnJXkH9h/edit?usp=sharing&ouid=109916020186826423742&rtpof=true&sd=true",
      "noopener,noreferrer",
    );
  };

  const headCells = [
    { id: "id", label: "No", sortable: true },
    { id: "cronName", label: "Cron Name", sortable: true },
    { id: "status", label: "Status", sortable: true },
    { id: "lastRun", label: "Executed Time", sortable: true },
    { id: "timeTaken", label: "Time Taken", sortable: true },
    { id: "info", label: "Info", sortable: false },
  ];

  const getResponsiveTableCellProps = () => {
    if (isMobile)
      return { padding: "normal", sx: { fontSize: "0.75rem", py: 1.5, px: 1 } };
    if (isTablet)
      return {
        padding: "normal",
        sx: { fontSize: "0.8125rem", py: 1.5, px: 1.5 },
      };
    return { padding: "normal", sx: { fontSize: "0.875rem", py: 2, px: 2 } };
  };
  const tableCellProps = getResponsiveTableCellProps();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: "100%", pt: 0 }}>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              onClick={() => handleStatusFilter("all")}
              sx={{
                p: 2,
                borderRadius: 3,
                cursor: "pointer",
                height: "100%",
                borderLeft: `5px solid ${PRIMARY_COLOR}`,
                background:
                  statusFilter === "all"
                    ? `linear-gradient(135deg, #e3f2fd, #ffffff)`
                    : "#fff",
                boxShadow: 3,
                transition: "0.25s",
                "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{ p: 1.2, borderRadius: 2, backgroundColor: "#e3f2fd" }}
                  >
                    <BoltIcon sx={{ color: PRIMARY_COLOR, fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={600} fontSize="0.95rem">
                      Total Executions
                    </Typography>
                    <Box>
                      {selectedDate.isSame(dayjs(), "day") ? (
                        <Typography variant="caption" color="text.secondary">
                          {dayjs().format("DD MMM, hh:mm A")}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {selectedDate.format("DD MMM")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h5" fontWeight={700} color="#1976d2">
                    {totalAllCrons}
                  </Typography>
{/* 
                  {expectedCronCount > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          totalAllCrons === expectedCronCount
                            ? "#4caf50"
                            : totalAllCrons > expectedCronCount
                            ? "#ff9800"
                            : "#2196f3",
                      }}
                    >
                      {totalAllCrons === expectedCronCount
                        ? "✓ Done"
                        : totalAllCrons > expectedCronCount
                        ? `+${totalAllCrons - expectedCronCount} extra`
                        : `${expectedCronCount - totalAllCrons} pending`}
                    </Typography>
                  )} */}
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              onClick={() => handleStatusFilter("success")}
              sx={{
                p: 2,
                borderRadius: 3,
                cursor: "pointer",
                height: "100%",
                borderLeft: `5px solid ${SUCCESS_COLOR}`,
                background:
                  statusFilter === "success"
                    ? "linear-gradient(135deg, #e8f5e9, #ffffff)"
                    : "#fff",
                boxShadow: 3,
                transition: "0.25s",
                "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{ p: 1.2, borderRadius: 2, backgroundColor: "#e8f5e9" }}
                  >
                    <CheckCircleIcon
                      sx={{ color: SUCCESS_COLOR, fontSize: 22 }}
                    />
                  </Box>
                  <Box>
                    <Typography fontWeight={600} fontSize="0.95rem">
                      Success
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Jobs completed
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 800,
                    color: SUCCESS_COLOR,
                    minWidth: "60px",
                    textAlign: "right",
                  }}
                >
                  {count_success}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              onClick={() => handleStatusFilter("failed")}
              sx={{
                p: 2,
                borderRadius: 3,
                cursor: "pointer",
                height: "100%",
                borderLeft: `5px solid ${ERROR_COLOR}`,
                background:
                  statusFilter === "failed"
                    ? "linear-gradient(135deg, #fdecea, #ffffff)"
                    : "#fff",
                boxShadow: 3,
                transition: "0.25s",
                "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{ p: 1.2, borderRadius: 2, backgroundColor: "#fdecea" }}
                  >
                    <CancelIcon sx={{ color: ERROR_COLOR, fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={600} fontSize="0.95rem">
                      Failures
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Jobs failed
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 800,
                    color: ERROR_COLOR,
                    minWidth: "60px",
                    textAlign: "right",
                  }}
                >
                  {count_failure}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Filter indicator */}
        {statusFilter !== "all" && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`Filtered by: ${
                statusFilter === "success" ? "Success" : "Failure"
              }`}
              size="small"
              sx={{
                backgroundColor:
                  statusFilter === "success" ? SUCCESS_COLOR : ERROR_COLOR,
                color: "white",
              }}
            />
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              Clear Filters
            </Button>
          </Box>
        )}

        {/* Filters and Search */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ width: "100%", flexWrap: "nowrap", mb: 2 }}
        >
          <DatePicker
            label="Select Date"
            value={selectedDate}
            minDate={dayjs("2026-05-17")}
            maxDate={dayjs()}
            onChange={(newValue) => {
              if (newValue) {
                setSelectedDate(newValue);
                setPage(1);
                setStatusFilter("all");
                setUserSearch("");
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{
                  minWidth: 180,
                  width: { xs: "100%", md: "180px" },
                  "& .MuiInputBase-root": { height: "40px" },
                }}
              />
            )}
          />
          <TextField
            value={usersearch}
            onChange={handleSearch}
            placeholder="Search by cron name..."
            size="small"
            variant="outlined"
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Button
            variant="contained"
            onClick={handleViewDocumentation}
            startIcon={<PictureAsPdfIcon />}
            sx={{
              backgroundColor: PRIMARY_COLOR,
              color: "white",
              whiteSpace: "nowrap",
              height: "40px",
              "&:hover": { backgroundColor: "#0555c4" },
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            View Documentation
          </Button>
        </Stack>

        {/* Error / Empty States */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {!error && !loading && transformedData.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No cron job data available for {selectedDate.format("YYYY-MM-DD")}
          </Alert>
        )}
        {!error &&
          !loading &&
          transformedData.length > 0 &&
          filteredData.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No matching cron jobs found
            </Alert>
          )}

        {/* Table */}
        {!error &&
          !loading &&
          transformedData.length > 0 &&
          filteredData.length > 0 && (
            <Box
              sx={{
                width: "100%",
                mb: 2,
                boxShadow: 3,
                borderRadius: 2,
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
              <TableContainer
                sx={{ minWidth: isMobile ? 600 : isTablet ? 700 : "100%" }}
              >
                <Table
                  stickyHeader
                  sx={{ borderCollapse: "collapse", marginTop: 0 }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: PRIMARY_COLOR }}>
                      {headCells.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          sortDirection={
                            orderBy === headCell.id ? order : false
                          }
                          sx={{
                            fontWeight: "bold",
                            color: "#fff !important",
                            backgroundColor: PRIMARY_COLOR,
                            borderBottom: "1px solid rgba(255,255,255,0.1)",
                            whiteSpace: "nowrap",
                            ...tableCellProps.sx,
                          }}
                        >
                          {headCell.sortable ? (
                            <TableSortLabel
                              active={orderBy === headCell.id}
                              direction={
                                orderBy === headCell.id ? order : "asc"
                              }
                              onClick={createSortHandler(headCell.id)}
                              sx={{
                                color: "white !important",
                                "& .MuiTableSortLabel-icon": {
                                  color: "white !important",
                                },
                              }}
                            >
                              {headCell.label}
                              {orderBy === headCell.id && (
                                <Box component="span" sx={visuallyHidden}>
                                  {order === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                                </Box>
                              )}
                            </TableSortLabel>
                          ) : (
                            headCell.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: "white" }}>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((cron) => (
                        <TableRow
                          key={cron.id}
                          hover
                          sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                        >
                          <TableCell {...tableCellProps}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium", fontSize: "inherit" }}
                            >
                              {cron.id}
                            </Typography>
                          </TableCell>
                          <TableCell {...tableCellProps}>
                            <Tooltip
                              title={cron.cronName}
                              placement="top-start"
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "inherit",
                                  wordBreak: "break-word",
                                  maxWidth: isMobile
                                    ? 150
                                    : isTablet
                                    ? 200
                                    : "none",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {cron.cronName}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell {...tableCellProps}>
                            <Chip
                              label={cron.status}
                              size={isMobile ? "small" : "medium"}
                              sx={{
                                backgroundColor: getStatusColor(cron.status),
                                color: "white",
                                fontWeight: "bold",
                                minWidth: isMobile ? "60px" : "80px",
                                height: isMobile ? "24px" : "32px",
                                fontSize: isMobile ? "0.7rem" : "0.75rem",
                              }}
                            />
                          </TableCell>
                          <TableCell {...tableCellProps}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "inherit",
                                whiteSpace: isMobile ? "nowrap" : "normal",
                              }}
                            >
                              {formatDateTime(cron.lastRun)}
                            </Typography>
                          </TableCell>
                          <TableCell {...tableCellProps}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AccessTimeIcon
                                sx={{
                                  fontSize: isMobile ? 14 : 16,
                                  color: "#666",
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "inherit" }}
                              >
                                {formatDuration(cron.timeTaken)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell {...tableCellProps}>
                            <Tooltip
                              title={
                                cron.errorMessage
                                  ? "View Details"
                                  : "No Details"
                              }
                            >
                              <IconButton
                                size={isMobile ? "small" : "medium"}
                                onClick={() =>
                                  cron.errorMessage &&
                                  setSelectedLog(cron.rawData)
                                }
                                disabled={!cron.errorMessage}
                                sx={{
                                  color: cron.errorMessage
                                    ? PRIMARY_COLOR
                                    : "#ccc",
                                  p: isMobile ? 0.5 : 1,
                                }}
                              >
                                {cron.errorMessage ? (
                                  <ErrorOutlineIcon
                                    sx={{ fontSize: isMobile ? 18 : 24 }}
                                  />
                                ) : (
                                  <InfoOutlinedIcon
                                    sx={{ fontSize: isMobile ? 18 : 24 }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {sortedData.length > rowsPerPage && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "white",
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    variant="outlined"
                    shape="rounded"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        borderColor: PRIMARY_COLOR,
                        color: PRIMARY_COLOR,
                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                      },
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: PRIMARY_COLOR,
                        color: "white",
                        "&:hover": { backgroundColor: PRIMARY_COLOR },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

        {/* Details Dialog */}
        <Dialog
          open={Boolean(selectedLog)}
          onClose={() => setSelectedLog(null)}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: "hidden",
              m: { xs: 1.5, sm: 2 },
              width: "100%",
            },
          }}
        >
          {/* HEADER */}
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${PRIMARY_COLOR}, #0b63e5)`,
              color: "white",
              px: { xs: 2, sm: 3 },
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
                flex: 1,
              }}
            >
              {selectedLog?.error_message ? (
                <ErrorOutlineIcon sx={{ fontSize: 24, flexShrink: 0 }} />
              ) : (
                <InfoOutlinedIcon sx={{ fontSize: 24, flexShrink: 0 }} />
              )}

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.95rem", sm: "1.1rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {selectedLog?.cron_name || "-"}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "0.72rem", sm: "0.82rem" },
                    opacity: 0.9,
                    mt: 0.3,
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Cron Execution Details
                </Typography>
              </Box>
            </Box>

            <Chip
              label={selectedLog?.cron_status || "Unknown"}
              size="small"
              sx={{
                backgroundColor: getStatusColor(selectedLog?.cron_status),
                color: "#fff",
                fontWeight: 700,
                minWidth: 90,
              }}
            />
          </DialogTitle>

          {/* BODY */}
          <DialogContent
            sx={{
              p: { xs: 2, sm: 3 },
              backgroundColor: "#f8fafc",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              {/* EXECUTION INFO */}
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid #dbeafe",
                }}
              >
                {/* TOP BAR */}
                <Box
                  sx={{
                    backgroundColor: "#e3f2fd",
                    px: 2,
                    py: 1.2,
                    borderBottom: "1px solid #bfdbfe",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: PRIMARY_COLOR,
                      fontSize: 18,
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: PRIMARY_COLOR,
                      fontSize: "0.9rem",
                    }}
                  >
                    Execution Information
                  </Typography>
                </Box>

                {/* CONTENT */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      margin: 0,
                      fontFamily: "monospace",
                      fontSize: {
                        xs: "0.72rem",
                        sm: "0.82rem",
                      },
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      color: "#1e293b",
                    }}
                  >
                    {getCronInfo(selectedLog)}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </DialogContent>

          {/* FOOTER */}
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: "1px solid #e2e8f0",
              backgroundColor: "#fff",
            }}
          >
            <Button
              onClick={() => setSelectedLog(null)}
              variant="contained"
              sx={{
                background: `linear-gradient(135deg, ${PRIMARY_COLOR}, #0b63e5)`,
                borderRadius: 2,
                px: 3,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  background: `linear-gradient(135deg, #0555c4, ${PRIMARY_COLOR})`,
                  boxShadow: "none",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CronDashboard;
